/**
 * BACKEND SIMULADO - TEMPORAL.
 *
 * Todo este archivo desaparece cuando exista el backend real. Existe para que
 * la interfaz se pueda construir y probar completa sin esperar a la API.
 *
 * Imita deliberadamente el comportamiento incomodo de una red real -latencia,
 * fallos, codigos de error tipados- porque una interfaz probada solo contra
 * datos instantaneos y perfectos se rompe el dia que habla con un servidor.
 *
 * El estado se guarda en localStorage para que la simulacion sobreviva a un
 * refresco de pagina y la demo se sienta real.
 */

import { PLAN_ID, SUBSCRIPTION_STATUS } from '../../domain/subscription';
import { ApiError, ERROR_CODE } from '../apiError';
import {
  litersBetween, litersLast24h, litersThisMonth, flowRateNow, pressureNow, recentAlerts,
  profileOf, telemetrySeries, aggregatedByDay, aggregatedByDateRange,
} from './mockTelemetry';

const STORE_KEY = 'nissaguard.mock.v1';
const DEMO_PROFILE_VERSION = 2;
const DEMO_USAGE_VERSION = 1;
const LATENCY_MS = [180, 520];   // rango realista para API Gateway + Lambda

/* El contrato de errores vive en services/apiError: pertenece al backend real
   tanto como a esta simulacion. Se reexporta para no romper a quien ya lo
   importaba desde aqui. */
export { ApiError, ERROR_CODE } from '../apiError';

const delay = () =>
  new Promise((r) => setTimeout(r, LATENCY_MS[0] + Math.random() * (LATENCY_MS[1] - LATENCY_MS[0])));

/* ------------------------------------------------------------------ */
/* Catalogo de dispositivos "fabricados"                               */
/*                                                                     */
/* Simula lo que en produccion seria una tabla de inventario escrita en */
/* fabrica: que numeros de serie existen y cual es su codigo de         */
/* activacion. El usuario nunca ve esta tabla.                          */
/* ------------------------------------------------------------------ */
const FACTORY_INVENTORY = [
  { serial: 'NG-001-4F2A', code: 'A7K2-9XQ4', model: 'NisaMeter v2', claimedBy: 'me' },
  { serial: 'NG-002-8B31', code: 'M4P8-2LT6', model: 'NisaMeter v2', claimedBy: 'me' },
  { serial: 'NG-003-C094', code: 'R9D3-7WZ1', model: 'NisaMeter v2', claimedBy: null },
  { serial: 'NG-004-E7A8', code: 'K2N6-4YB9', model: 'NisaMeter v2', claimedBy: null },
  { serial: 'NG-005-11D6', code: 'T8V5-3QH2', model: 'NisaMeter Pro', claimedBy: null },
  // Ya pertenece a otra cuenta: sirve para probar el caso de conflicto.
  { serial: 'NG-006-9F3C', code: 'W6J1-8ZK5', model: 'NisaMeter v2', claimedBy: 'otro-usuario' },
];

const ahora = () => Math.floor(Date.now() / 1000);

/** Dispositivos con los que arranca la cuenta simulada. */
function estadoInicial() {
  return {
    demo_profile_version: DEMO_PROFILE_VERSION,
    demo_usage_version: DEMO_USAGE_VERSION,
    devices: [
      {
        device_id: 'nissa-guard-001',
        serial: 'NG-001-4F2A',
        device_name: 'NisaGuard Edificio 9',
        location: 'Instituto Tecnologico de Nuevo Leon',
        firmware_version: '2.0.0',
        last_seen: ahora() - 20,
        valve_state: 'open',
        valve_events: [],
        wifi_rssi: -58,
        flow_liters: 0,
        daily_consumption: 0,
        monthly_consumption: 0,
        linked_at: ahora() - 86400 * 120,
        configuration: { max_flow_rate: 100, max_daily_usage: 8000, valve_timeout: 0 },
      },
      {
        device_id: 'nissa-guard-002',
        serial: 'NG-002-8B31',
        device_name: 'NisaGuard Exposicion',
        location: 'Instituto Tecnologico de Ciudad Madero',
        firmware_version: '2.0.0',
        last_seen: ahora() - 20,
        valve_state: 'open',
        valve_events: [],
        wifi_rssi: -71,
        flow_liters: 0,
        daily_consumption: 0,
        monthly_consumption: 0,
        linked_at: ahora() - 86400 * 120,
        configuration: { max_flow_rate: 60, max_daily_usage: 6000, valve_timeout: 0 },
      },
    ],
    subscription: {
      plan_id: PLAN_ID.FREE,
      status: SUBSCRIPTION_STATUS.ACTIVE,
      current_period_start: new Date(Date.now() - 86400e3 * 12).toISOString(),
      current_period_end: new Date(Date.now() + 86400e3 * 18).toISOString(),
      cancel_at_period_end: false,
      payment_method: null,
    },
    inventory: FACTORY_INVENTORY,
  };
}

function leer() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) {
      const inicial = estadoInicial();
      localStorage.setItem(STORE_KEY, JSON.stringify(inicial));
      return inicial;
    }
    const estado = JSON.parse(raw);
    // Aplica el nuevo escenario a demos ya guardadas sin tocar otros equipos.
    if ((estado.demo_profile_version ?? 0) < DEMO_PROFILE_VERSION) {
      const perfiles = {
        'nissa-guard-001': {
          device_name: 'NisaGuard Edificio 9',
          location: 'Instituto Tecnologico de Nuevo Leon',
          max_daily_usage: 8000,
        },
        'nissa-guard-002': {
          device_name: 'NisaGuard Exposicion',
          location: 'Instituto Tecnologico de Ciudad Madero',
          max_daily_usage: 6000,
        },
      };
      for (const device of estado.devices ?? []) {
        const perfil = perfiles[device.device_id];
        if (!perfil) continue;
        device.device_name = perfil.device_name;
        device.location = perfil.location;
        device.configuration = { ...(device.configuration ?? {}), max_daily_usage: perfil.max_daily_usage };
        device.valve_state = 'open';
      }
      estado.demo_profile_version = DEMO_PROFILE_VERSION;
    }
    if ((estado.demo_usage_version ?? 0) < DEMO_USAGE_VERSION) {
      for (const device of estado.devices ?? []) {
        if (device.last_seen === 0) continue;
        device.valve_events = device.valve_state === 'closed'
          ? [{ at: Date.now(), state: 'closed' }]
          : [];
        if (device.device_id === 'nissa-guard-002') {
          device.linked_at = Math.min(Number(device.linked_at) || ahora(), ahora() - 86400 * 120);
        }
      }
      estado.demo_usage_version = DEMO_USAGE_VERSION;
    }
    // Migra demos guardadas por versiones anteriores para que la interfaz no
    // siga mostrando un temporizador automático que ya no existe.
    for (const device of estado.devices ?? []) {
      device.configuration = {
        ...(device.configuration ?? {}),
        valve_timeout: 0,
        valve_timeout_enabled: false,
        abnormal_flow_auto_close_enabled: false,
      };
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(estado));
    return estado;
  } catch {
    return estadoInicial();
  }
}

function escribir(estado) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(estado));
  } catch {
    /* cuota agotada: la simulacion sigue en memoria durante la sesion */
  }
}

const alertOptions = (device, now = new Date()) => ({
  events: device.valve_events ?? [],
  since: device.linked_at * 1000,
  wifiRssi: device.wifi_rssi,
  now,
});

/** Reinicia la simulacion. Util para demostraciones y pruebas manuales. */
export function resetMockBackend() {
  localStorage.removeItem(STORE_KEY);
  return leer();
}

/**
 * Rellena cada dispositivo con las cifras del generador de telemetria.
 *
 * Es la pieza que garantiza la CONSISTENCIA: el consumo que muestra la lista de
 * dispositivos, el que ve el panel de un equipo concreto y el que suma la vista
 * de cuenta salen todos de `mockTelemetry`, no de incrementos aleatorios
 * independientes. Antes cada lectura sumaba un valor al azar y las tres vistas
 * divergian entre si.
 */
function simularActividad(estado) {
  const t = ahora();
  const now = new Date();
  const resueltas = new Set(estado.resolvedAlerts ?? []);

  estado.devices = estado.devices.map((d) => {
    const nuncaVisto = d.last_seen === 0;

    // Los equipos de la demo siguen emitiendo aunque la página lleve horas cerrada.
    // Un equipo recién vinculado todavía no tiene telemetría.
    if (nuncaVisto) {
      return { ...d, flow_rate_lpm: 0, pressure_psi: 0, alerts_7d: 0 };
    }

    const abierta = d.valve_state === 'open';
    const events = d.valve_events ?? [];
    const since = d.linked_at * 1000;
    const alertas = recentAlerts(d.device_id, 7, alertOptions(d, now))
      .filter((alert) => !resueltas.has(alert.id));
    const semana = aggregatedByDay(d.device_id, 7, events, now, since);
    const consumoHoy = semana[semana.length - 1]?.total_liters ?? 0;
    const consumoSemanal = +semana.reduce((total, day) => total + day.total_liters, 0).toFixed(2);
    const consumoMensual = litersThisMonth(d.device_id, now, events, since);

    return {
      ...d,
      last_seen: t - 5,
      daily_consumption: consumoHoy,
      weekly_consumption: consumoSemanal,
      last_24h_consumption: litersLast24h(d.device_id, now, events, since),
      monthly_consumption: consumoMensual,
      flow_liters: litersBetween(d.device_id, since, now.getTime(), events),
      flow_rate_lpm: abierta ? flowRateNow(d.device_id, now) : 0,
      pressure_psi: pressureNow(d.device_id, abierta, now),
      alerts_7d: alertas.length,
      last_alert: alertas[0] ?? null,
      usage_profile: profileOf(d.device_id).label,
      wifi_rssi: d.wifi_rssi,
    };
  });
  return estado;
}

/* ------------------------------------------------------------------ */
/* API simulada                                                        */
/* Las firmas replican las del backend futuro para que el repositorio  */
/* pueda cambiar de fuente sin que la interfaz se entere.              */
/* ------------------------------------------------------------------ */

export const mockApi = {
  async listDevices() {
    await delay();
    const estado = simularActividad(leer());
    escribir(estado);
    return estado.devices;
  },

  async getDevice(deviceId) {
    await delay();
    const estado = simularActividad(leer());
    const d = estado.devices.find((x) => x.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');
    return d;
  },

  /** Telemetría cruda de un dispositivo. Alimenta el panel y el histórico. */
  async getTelemetry(deviceId, { hours = 24, limit = 500 } = {}) {
    await delay();
    const d = leer().devices.find((x) => x.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');
    if (d.last_seen === 0) return { data: [], count: 0 };
    const serie = telemetrySeries(deviceId, {
      hours, events: d.valve_events ?? [], since: d.linked_at * 1000,
    });
    const recortada = serie.slice(-limit);
    return { data: [...recortada].reverse(), count: recortada.length };
  },

  /** Agregado por día, para las gráficas de historial y comparativas. */
  async getAggregatedTelemetry(deviceId, { days = 7, startDate, endDate } = {}) {
    await delay();
    const d = leer().devices.find((x) => x.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');
    if (d.last_seen === 0) return { data: [], aggregated: true };
    const events = d.valve_events ?? [];
    const since = d.linked_at * 1000;
    const data = startDate
      ? aggregatedByDateRange(deviceId, startDate, endDate ?? startDate, events, new Date(), since)
      : aggregatedByDay(deviceId, days, events, new Date(), since);
    return { data, aggregated: true };
  },

  /** Alertas recientes del dispositivo. */
  async getDeviceAlerts(deviceId, { days = 7 } = {}) {
    await delay();
    const estado = leer();
    const device = estado.devices.find((item) => item.device_id === deviceId);
    if (!device) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');
    const resueltas = new Set(estado.resolvedAlerts ?? []);
    return {
      alerts: recentAlerts(deviceId, days, alertOptions(device)).map((alert) => ({
        ...alert,
        resolved: resueltas.has(alert.id),
      })),
    };
  },

  /**
   * Alertas de toda la cuenta, equivalente simulado de `GET /alerts`.
   *
   * Se generan por dispositivo y se juntan, para que el total del menu lateral
   * sea la suma de lo que muestra cada equipo y no un numero suelto.
   */
  async getAccountAlerts({ days = 7 } = {}) {
    await delay();
    const estado = leer();
    const resueltas = new Set(estado.resolvedAlerts ?? []);
    const now = new Date();
    return estado.devices
      .flatMap((d) => recentAlerts(d.device_id, days, alertOptions(d, now)))
      .map((a) => ({ ...a, resolved: resueltas.has(a.id) }))
      .sort((a, b) => b.timestamp - a.timestamp);
  },

  /** Resolver persiste en el estado simulado: al recargar sigue resuelta. */
  async resolveAlert(deviceId, alertId) {
    await delay();
    const estado = leer();
    estado.resolvedAlerts = [...new Set([...(estado.resolvedAlerts ?? []), alertId])];
    escribir(estado);
    return { resolved: true, alert_id: alertId };
  },

  /**
   * Vinculacion por numero de serie + codigo de activacion.
   *
   * Reproduce las comprobaciones que el backend real tendra que hacer, y en el
   * mismo orden, porque el orden importa: no se debe revelar que un serial
   * existe antes de validar el codigo.
   */
  async linkDevice({ serial, code }) {
    await delay();
    const estado = leer();
    const s = String(serial || '').trim().toUpperCase();
    const c = String(code || '').trim().toUpperCase();

    const item = estado.inventory.find((i) => i.serial === s);

    // El serial no existe o el codigo no coincide: MISMO error en ambos casos.
    // Distinguirlos permitiria averiguar que seriales existen probando codigos.
    if (!item || item.code !== c) {
      throw new ApiError(
        ERROR_CODE.INVALID_CODE,
        'El número de serie o el código de activación no son correctos'
      );
    }
    if (item.claimedBy === 'me') {
      throw new ApiError(ERROR_CODE.ALREADY_LINKED_BY_YOU, 'Ya tienes este dispositivo vinculado');
    }
    if (item.claimedBy) {
      throw new ApiError(
        ERROR_CODE.ALREADY_LINKED_BY_OTHER,
        'Este dispositivo pertenece a otra cuenta'
      );
    }

    const n = estado.devices.length + 1;
    const nuevo = {
      device_id: `nissa-guard-${String(n).padStart(3, '0')}`,
      serial: item.serial,
      device_name: `Dispositivo ${n}`,
      location: '',
      firmware_version: '2.0.0',
      last_seen: 0,                       // aun no ha emitido: estado "nunca conectado"
      valve_state: 'open',
      wifi_rssi: null,
      flow_liters: 0,
      daily_consumption: 0,
      monthly_consumption: 0,
      linked_at: ahora(),
      configuration: { max_flow_rate: 100, max_daily_usage: 1000, valve_timeout: 0 },
    };

    item.claimedBy = 'me';
    estado.devices.push(nuevo);
    escribir(estado);
    return nuevo;
  },

  async unlinkDevice(deviceId) {
    await delay();
    const estado = leer();
    const d = estado.devices.find((x) => x.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');

    estado.devices = estado.devices.filter((x) => x.device_id !== deviceId);
    const item = estado.inventory.find((i) => i.serial === d.serial);
    if (item) item.claimedBy = null;       // vuelve a estar disponible
    escribir(estado);
    return { ok: true };
  },

  /**
   * Control de valvula simulado.
   *
   * Normaliza "close" a "closed" igual que hace el firmware corregido, para que
   * la demo refleje el comportamiento real del dispositivo y no una version
   * idealizada donde todo funciona.
   */
  async controlValve(deviceId, action) {
    await delay();
    const estado = leer();
    const d = estado.devices.find((x) => x.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');

    let destino;
    if (action === 'open') destino = 'open';
    else if (action === 'close') destino = 'closed';
    else throw new ApiError(ERROR_CODE.INVALID_CODE, `Acción no reconocida: ${action}`);

    if (d.valve_state !== destino) {
      d.valve_events = [...(d.valve_events ?? []), { at: Date.now(), state: destino }];
      d.valve_state = destino;
    }
    escribir(estado);
    return { status: 'executed', device_id: deviceId, new_valve_state: destino };
  },

  async updateDeviceConfig(deviceId, changes) {
    await delay();
    const estado = leer();
    const d = estado.devices.find((item) => item.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');

    const requested = changes?.configuration ?? changes ?? {};
    d.configuration = { ...(d.configuration ?? {}), ...requested };
    escribir(estado);
    return d;
  },

  async renameDevice(deviceId, { name, location }) {
    await delay();
    const estado = leer();
    const d = estado.devices.find((x) => x.device_id === deviceId);
    if (!d) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');
    if (name !== undefined) d.device_name = name;
    if (location !== undefined) d.location = location;
    escribir(estado);
    return d;
  },

  async getSubscription() {
    await delay();
    return leer().subscription;
  },

  /** Simula el flujo de alta. El pago real nunca pasara por el navegador. */
  async subscribe(planId, { simulateFailure = false } = {}) {
    await delay();
    if (simulateFailure) {
      throw new ApiError(ERROR_CODE.PAYMENT_DECLINED, 'El pago fue rechazado por el banco');
    }
    const estado = leer();
    estado.subscription = {
      plan_id: planId,
      status: SUBSCRIPTION_STATUS.ACTIVE,
      current_period_start: new Date().toISOString(),
      current_period_end: new Date(Date.now() + 86400e3 * 30).toISOString(),
      cancel_at_period_end: false,
      payment_method:
        planId === PLAN_ID.FREE
          ? null
          : { brand: 'visa', last4: '4242', exp_month: 12, exp_year: 2029 },
    };
    escribir(estado);
    return estado.subscription;
  },

  async cancelSubscription() {
    await delay();
    const estado = leer();
    estado.subscription = {
      ...estado.subscription,
      status: SUBSCRIPTION_STATUS.CANCELED,
      cancel_at_period_end: true,
    };
    escribir(estado);
    return estado.subscription;
  },

  async resumeSubscription() {
    await delay();
    const estado = leer();
    estado.subscription = {
      ...estado.subscription,
      status: SUBSCRIPTION_STATUS.ACTIVE,
      cancel_at_period_end: false,
    };
    escribir(estado);
    return estado.subscription;
  },
};

/** Codigos validos, expuestos solo para la ayuda en pantalla de la demo. */
export function codigosDePrueba() {
  return leer().inventory.map(({ serial, code, claimedBy }) => ({
    serial,
    code,
    estado: claimedBy === 'me' ? 'ya vinculado' : claimedBy ? 'de otra cuenta' : 'disponible',
  }));
}
